/* eslint-disable @typescript-eslint/no-unused-vars */
'use client';
/* eslint-disable import/no-unresolved */

// The rule above to shut ESLint from complaining
// about unresolved Swiper's CSS imports
// Why? see: https://github.com/import-js/eslint-plugin-import/issues/2266

import React, { useEffect, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/nextjs';
import { getCookie } from 'cookies-next';
import Row from 'antd/es/row';
import Col from 'antd/es/col';

import { FlexibleDiv, Row as BaseRow } from '@components/formStyles';
import SortingTabs from '@shared/SortingTabs';
import { CAR_SORT_DATA } from '@constants/constants';
import STATIC_ROUTES from '@data/staticRoutes.json';
import { CarRentalCard, ScrollToTop } from '@shared';
import NotFound from '@components/Errors/404/404';
import { getActiveSortFromQuery } from '@utils/carRentalFilterUtils';
import {
  CarRentalCarFeatureProps,
  CarRentalCarReservationProps,
} from '@shared/CarRentalCard/CarRentalCardProps';
import useBasketInformation from '@hooks/useBasketInformation';
import Loader from '@components/Loader/Loader';
import { useToggle } from '@hooks/common';
import ConfirmationModal from '@components/ui/modals/ConfirmationModal';
import { clearBasketStorage } from '@utils/basket';
import useUnlockBasket from '@components/views/hooks/useUnlockBasket';
import { clearBasket } from '@utils/clearBasket';
import ErrorInfoModal from '@components/views/components/ErrorInfoModal/ErrorInfoModal';
import {
  ApiError,
  VehicleConditionViewModel,
  VehicleGroupViewModel,
  VehiclePrebookAddRequestViewModel,
  VehiclePrebookViewModel,
} from '@api/index';
import { callService } from '@api/caller';
import { DEFAULT_CAR_RENTAL_SEARCH_QUERY_PARAM_VALUES } from '@page/carRental/list/constants';
import { useAppRouter } from '@contexts/router';
import { useSearchParamsToQuery } from '@hooks/useSearchParamsToQuery';
import { CarRentalListPageProps } from '@page/carRental/list';
import Pagination from '@components/Pagination/next/Pagination';
import { useBreakpoint } from '@contexts/breakpoints';
import CarRentalCardSkeleton from '@shared/CarRentalCard/CarRentalCardSkeleton';

import {
  CAR_RENTAL_LISTING_DEFAULT_PAGE,
  CAR_RENTAL_SEARCH_QUERY_PARAM_KEYS,
} from '../searchBar/list/constants';
import {
  BasketErrorText,
  NotFoundWrapper,
  ResultsWrapper,
} from './components/Results.styled';
import { generateDateTimeQuery } from '../searchBar/list/utils';
import { getParsedListData } from '../utils';
import 'swiper/css';

export interface SortInterface {
  id: string;
  label: string;
  disabled: boolean;
}

const CarRentalListResults = ({
  is404 = false,
  initialCarRentalSSRData,
}: CarRentalListPageProps) => {
  const { xl: isDesktop } = useBreakpoint();
  const query = useSearchParamsToQuery();
  const { push, isLoading } = useAppRouter();
  const [selectedVehicle, setSelectedVehicle] =
    useState<VehicleGroupViewModel | null>(null);
  const { vehicles, meta } = getParsedListData(initialCarRentalSSRData);

  const { basketInfo, basketId, itemsInBasket } = useBasketInformation();
  const [
    confirmFullBasketModal,
    toggleConfirmFullBasketModal,
    setConfirmFullBasketModal,
  ] = useToggle();
  const { unlockBasketAsync, cleanBasketSignal } = useUnlockBasket({
    basketId: basketId || '',
  });

  const [sort, setSort] = useState<SortInterface>(
    getActiveSortFromQuery(query)
  );
  const [isRemoveVehicleLoading, setIsRemoveVehicleLoading] = useState(false);
  const [errorModalStatusMessage, setErrorModalStatusMessage] =
    useState<string>('');

  useEffect(() => {
    setSort(getActiveSortFromQuery(query));
  }, [query]);

  const addBasket = async (): Promise<VehiclePrebookViewModel> => {
    const basketId =
      typeof window !== 'undefined' && localStorage.getItem('basketId');

    const body: VehiclePrebookAddRequestViewModel = {
      providerId: selectedVehicle?.providerId || 0,
      providerName: selectedVehicle?.providerName || '',
      reservation: {
        rateCode: selectedVehicle?.reservation?.rateCode || '',
        pickUpDate: generateDateTimeQuery(
          query[CAR_RENTAL_SEARCH_QUERY_PARAM_KEYS.PICK_UP_DATE] ||
            DEFAULT_CAR_RENTAL_SEARCH_QUERY_PARAM_VALUES.pickUpDate,
          query[CAR_RENTAL_SEARCH_QUERY_PARAM_KEYS.PICK_UP_TIME] ||
            DEFAULT_CAR_RENTAL_SEARCH_QUERY_PARAM_VALUES.pickUpTime
        ),
        pickupLocation: query[
          CAR_RENTAL_SEARCH_QUERY_PARAM_KEYS.PICK_UP_LOCATION
        ] as string,
        dropoffDate: generateDateTimeQuery(
          query[CAR_RENTAL_SEARCH_QUERY_PARAM_KEYS.DROP_OFF_DATE] ||
            DEFAULT_CAR_RENTAL_SEARCH_QUERY_PARAM_VALUES.dropOffDate,
          query[CAR_RENTAL_SEARCH_QUERY_PARAM_KEYS.DROP_OFF_TIME] ||
            DEFAULT_CAR_RENTAL_SEARCH_QUERY_PARAM_VALUES.dropOffTime
        ),
        dropoffLocation: query[
          CAR_RENTAL_SEARCH_QUERY_PARAM_KEYS.DROP_OFF_LOCATION
        ] as string,
        price: {
          requestId: selectedVehicle?.reservation?.requestId || '',
          fee: selectedVehicle?.reservation?.price?.price || 0,
          totalFee: selectedVehicle?.reservation?.price?.totalPrice || 0,
          oneWayFee: selectedVehicle?.reservation?.price?.oneWayFee || 0,
          currency: selectedVehicle?.reservation?.price?.currency || '',
          discountedFee:
            selectedVehicle?.reservation?.price?.discountedPrice || 0,
          costFee: selectedVehicle?.reservation?.price?.costPrice || 0,
        },
        dayCount: selectedVehicle?.reservation?.dayCount || 0,
      },
      vehicle: {
        transmissionType: selectedVehicle?.feature?.transmissionType || '',
        fuelType: selectedVehicle?.feature?.fuelType || '',
        maxKilometer: selectedVehicle?.reservation?.maxKilometer || 0,
        vehiclePeer: {
          name: selectedVehicle?.vehicles?.[0]?.name || '',
          image: selectedVehicle?.vehicles?.[0]?.image || '',
        },
        class: {
          code: selectedVehicle?.classCode || '',
          name: selectedVehicle?.className || '',
          size: selectedVehicle?.classSize || '',
          category: selectedVehicle?.classCategory || '',
        },
        group: {
          code: selectedVehicle?.code || '',
          name: selectedVehicle?.name || '',
        },
      },
      transactionId: selectedVehicle?.transactionId || '',
    };

    await unlockBasketAsync();

    const basketState = await cleanBasketSignal;

    return callService('PrebookService', 'prebookAddVehicle', {
      basketId: basketState === 'stale' || !basketId ? '' : basketId,
      body,
    });
  };

  const prebookMutation = useMutation({
    mutationKey: ['carRentalPrebook'],
    mutationFn: addBasket,
    onSuccess: (res) => {
      if (res?.basketId) {
        if (typeof window !== 'undefined')
          localStorage.setItem('basketId', res.basketId);
        push(`/seyahat-planlama?bid=${res.basketId}`);
      }
    },
    onError: (error: ApiError) => {
      setErrorModalStatusMessage(error?.body?.errors?.[0]?.detail);
      const sessionId = getCookie('sessionId');
      const sentryDate = new Date();

      Sentry.addBreadcrumb({
        level: 'info',
        message: 'PrebookService.prebookAddVehicle Sepet oluşturulamadı',
        category: 'PrebookService',
        data: { error, sessionId },
      });
      Sentry.captureMessage(
        `ERROR : Araç Kiralama Sepeti oluşturulamadı : ${sentryDate.toLocaleDateString()}`,
        'error'
      );
      Sentry.captureMessage(`MUSTERI - ${sessionId}`, 'info');
    },
  });

  const handleNavigateTravelPlanning = async (): Promise<void> =>
    prebookMutation.mutate();

  const handleTabClick = (id: string): void => {
    const { page, ...nextQuery } = query;
    const item = CAR_SORT_DATA.find((item) => item.id === id);

    if (!item) {
      return;
    }

    setSort(item);

    push({
      pathname: STATIC_ROUTES.carRentalSearchPathPrefix,
      query: { ...nextQuery, sort: item.id },
    });
  };

  const errorInfoModalClosed = () => {
    setErrorModalStatusMessage('');
  };

  // =========================================
  // Clear basket hotel items before add new one
  // =========================================
  const handleClearBasketItems = async () => {
    try {
      if (basketInfo && basketId) {
        setIsRemoveVehicleLoading(true);

        if (basketInfo?.id && basketInfo.isLocked) {
          clearBasketStorage(true);
        } else {
          await unlockBasketAsync();
          await clearBasket(basketInfo, basketId);
        }

        toggleConfirmFullBasketModal();
        handleNavigateTravelPlanning();
      }
    } catch (error) {
      toggleConfirmFullBasketModal();
      clearBasketStorage(true);
    } finally {
      setIsRemoveVehicleLoading(false);
    }
  };

  const handleCarSelect = (vehicle: VehicleGroupViewModel) => {
    setSelectedVehicle(vehicle);

    if (itemsInBasket) {
      toggleConfirmFullBasketModal();
    } else {
      handleNavigateTravelPlanning();
    }
  };

  if (is404) {
    return (
      <NotFoundWrapper as='section' aria-label='Araç Bulunamadı'>
        <NotFound
          label='Aradığınız kriterlere uygun araç bulunamadı. Lütfen tekrar deneyiniz.'
          iconSize={124}
          className='carRentalList__notFound'
        />
      </NotFoundWrapper>
    );
  }

  return (
    <ResultsWrapper as='section' aria-label='Araç Kiralama Arama Sonuçları'>
      {is404 ? null : (
        <>
          {isDesktop ? (
            <FlexibleDiv top={24} bottom={24}>
              <BaseRow alignItems='center' justifyContent='flex-end'>
                <SortingTabs
                  activeKey={sort.id}
                  data={CAR_SORT_DATA}
                  handleTabClick={handleTabClick}
                />
              </BaseRow>
            </FlexibleDiv>
          ) : null}
        </>
      )}

      {isLoading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {[...Array(3)].map((_, i) => (
            <CarRentalCardSkeleton key={i} />
          ))}
        </div>
      ) : (
        <div>
          <section aria-label='Araç Kiralama Sonuçları'>
            <Row gutter={[24, 24]} justify='center'>
              {vehicles?.map((vehicle, i) => (
                <Col lg={24} key={i} flex={1}>
                  <CarRentalCard
                    className={vehicle?.className || ''}
                    vehicles={vehicle?.vehicles}
                    feature={vehicle?.feature as CarRentalCarFeatureProps}
                    condition={vehicle?.condition as VehicleConditionViewModel}
                    reservation={
                      vehicle?.reservation as CarRentalCarReservationProps
                    }
                    providerName={vehicle?.providerName || ''}
                    providerImageUrl={vehicle?.providerImageUrl || ''}
                    onCarSelect={() => handleCarSelect(vehicle)}
                  />
                </Col>
              ))}
            </Row>
          </section>
        </div>
      )}

      {meta?.totalPages && meta.totalPages > 1 && (
        <Pagination
          totalItemCount={meta.totalCount}
          currentPage={
            query.page ? +query.page : CAR_RENTAL_LISTING_DEFAULT_PAGE
          }
          totalPages={meta.totalPages}
          type='carRental'
        />
      )}

      {confirmFullBasketModal ? (
        <ConfirmationModal
          onCancel={() => setConfirmFullBasketModal(false)}
          onSubmit={handleClearBasketItems}
          content='Sepetinizde başka bir ürün bulunmakta, var olanı silip yenisini eklemek ister misiniz?'
          title='Sil'
          isConfirmDisabled={isRemoveVehicleLoading}
          isConfirmLoading={isRemoveVehicleLoading}
        />
      ) : null}

      {prebookMutation.isPending ? <Loader /> : null}
      {prebookMutation.isError ? (
        <ErrorInfoModal
          title='Ooops'
          description={
            <BasketErrorText>
              {errorModalStatusMessage !== ''
                ? errorModalStatusMessage
                : 'Sepete ürün eklemeye çalışırken, genel bir hata oluştu, tekrar deneyiniz.'}
            </BasketErrorText>
          }
          onCancel={errorInfoModalClosed}
        />
      ) : null}
      <ScrollToTop />
    </ResultsWrapper>
  );
};

export default CarRentalListResults;
